import './Collapsible.css';

import React, { FC } from 'react';
import { animated, useSpring } from 'react-spring';

import cn from 'classnames';
import { usePrevious } from 'vrlcm-gui-utils';
import { l10n } from '../with-app-root/i18n/i18nUtil';

interface Props {
  title: string | JSX.Element;
  children: string | JSX.Element;
  collapsed: boolean;
  destroyContentOnCollapse?: boolean;
  onToggle: () => void;
  metaTitle?: string | JSX.Element;
}

const Collapsible: FC<Props> = props => {
  const {
    children,
    title,
    onToggle,
    collapsed,
    destroyContentOnCollapse = false,
    metaTitle
  } = props;

  const expanded = !collapsed;

  const previous = usePrevious(expanded);
  const { height, opacity, transform } = useSpring({
    from: { height: 0, opacity: 0, transform: 'translate3d(24px,0,0)' },
    height: expanded ? 'auto' : 0,
    opacity: expanded ? 1 : 0,
    transform: `translate3d(${expanded ? 0 : 24}px,0,0)`
  });

  const onKeyUpToggle = (e: React.KeyboardEvent) =>
    (e.keyCode === 13 || e.keyCode === 32) && onToggle();

  const content = <animated.div style={{ transform }}>{children}</animated.div>;

  return (
    <div className={cn('vrlcm-collapsible-block', { expanded })}>
      <div
        className="vrlcm-collapsible-header"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyUp={onKeyUpToggle}
      >
        <clr-icon
          class="vrlcm-collapsible-header-icon"
          shape="caret"
          title={expanded ? l10n('collapse') : l10n('expand')}
        />
        <span className="vrlcm-collapsible-header-title">{title}</span>
        {metaTitle || null}
      </div>
      {expanded && (
        <animated.div
          className="collapsible-content"
          style={{
            opacity,
            height: expanded && previous === expanded ? 'auto' : height
          }}
        >
          {destroyContentOnCollapse ? (expanded ? content : null) : content}
        </animated.div>
      )}
    </div>
  );
};

export default Collapsible;
